import React, { useMemo, useState } from 'react';
import { Play, XCircle, ChevronDown } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '../../../../components/ui/popover';
import { getAuth } from 'firebase/auth';
import { toast } from 'sonner';
import {
  getAccountStatus,
  LIFECYCLE_LABELS,
  LIFECYCLE_PILL_CLASSES,
  formatRelativeTime,
  formatActorLabel,
  getLastStatusChange,
} from '../../../../utils/accountLifecycle';
import AccountCloseDialog from './AccountCloseDialog';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * AccountStatusMenu — V1 customer lifecycle action menu.
 *
 * V1 contract: operators see only two states on the customer-level UI —
 *   • Active (default)
 *   • Closed (relationship ended, captures a reason in S4)
 *
 * The backend retains 'inactive' as a valid enum value for future
 * flexibility, but no operator workflow sets or unsets it from here.
 * A lead that arrives in 'inactive' state (programmatically or via legacy
 * 'paused' data normalization) renders a read-only "Inactive" pill with a
 * "Reactivate to Active" affordance.
 *
 * Recurring-program suspension lives on the Service Plan card's
 * Active/Inactive toggle, NOT here.
 */
const AccountStatusMenu = ({ lead, readOnly = false }) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);

  const status = getAccountStatus(lead);
  const pillLabel = LIFECYCLE_LABELS[status];

  // Last status-change footnote — read-only.
  const lastChangeLine = useMemo(() => {
    const entry = getLastStatusChange(lead);
    if (!entry) return null;
    const rel = formatRelativeTime(entry.at);
    const actor = formatActorLabel(entry);
    if (!rel) return null;
    return `Last status change · ${rel}${actor ? ` by ${actor}` : ''}`;
  }, [lead]);

  const callApi = async (body) => {
    if (!lead?.id) return;
    setSubmitting(true);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      const res = await fetch(`${API}/api/leads/${lead.id}/account-status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || 'Failed to update account status');
        return;
      }
      toast.success(`Account ${LIFECYCLE_LABELS[body.status].toLowerCase()}`);
      setOpen(false);
    } catch (err) {
      console.error('[AccountStatusMenu] update error:', err);
      toast.error('Network error — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReactivate = () => callApi({ status: 'active', reason: 'Manual reactivate' });

  // S4: open the structured Close dialog instead of window.confirm.
  const handleOpenCloseDialog = () => {
    setOpen(false);
    setCloseDialogOpen(true);
  };

  const handleCloseDialogCancel = () => {
    if (submitting) return;
    setCloseDialogOpen(false);
  };

  // S4: confirm callback from AccountCloseDialog — hits the cascade endpoint.
  const handleCloseDialogConfirm = async ({ reason, reasonNote, cancelFutureVisits }) => {
    if (!lead?.id) return;
    setSubmitting(true);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      const res = await fetch(`${API}/api/leads/${lead.id}/account/close`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ reason, reasonNote, cancelFutureVisits }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || 'Failed to close account');
        return;
      }
      const cancelledCount = data?.cancelledTaskCount ?? 0;
      if (cancelFutureVisits && cancelledCount > 0) {
        toast.success(`Account closed · ${cancelledCount} future visit${cancelledCount === 1 ? '' : 's'} cancelled`);
      } else if (cancelFutureVisits) {
        toast.success('Account closed');
      } else {
        toast.success('Account closed · scheduled visits kept');
      }
      setCloseDialogOpen(false);
    } catch (err) {
      console.error('[AccountStatusMenu] close error:', err);
      toast.error('Network error — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  if (readOnly) {
    return (
      <span className="inline-flex flex-col items-start" data-testid="account-status-readonly">
        <span
          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${LIFECYCLE_PILL_CLASSES[status]}`}
          data-testid="account-status-pill"
        >
          {status === 'closed' && <XCircle className="h-3 w-3" />}
          {pillLabel}
        </span>
        {lastChangeLine && (
          <span className="text-[11px] text-gray-400 mt-1" data-testid="account-status-last-change">
            {lastChangeLine}
          </span>
        )}
      </span>
    );
  }

  return (
    <span className="inline-flex flex-col items-start">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border hover:opacity-80 transition-opacity ${LIFECYCLE_PILL_CLASSES[status]}`}
            data-testid="account-status-trigger"
            title="Change account status"
          >
            {status === 'closed' && <XCircle className="h-3 w-3" />}
            {pillLabel}
            <ChevronDown className="h-3 w-3 opacity-60" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-56 p-2" data-testid="account-status-menu">
          <div className="flex flex-col">
            {/* Reactivate appears whenever the account is not Active. Covers
                the 'closed' path and the rare programmatic 'inactive' path. */}
            {status !== 'active' && (
              <button
                type="button"
                onClick={handleReactivate}
                disabled={submitting}
                className="flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-emerald-50 text-emerald-700 disabled:opacity-50"
                data-testid="account-status-reactivate"
              >
                <Play className="h-4 w-4" /> Reactivate to Active
              </button>
            )}
            {status === 'active' && (
              <button
                type="button"
                onClick={handleOpenCloseDialog}
                disabled={submitting}
                className="flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-gray-100 text-gray-700 disabled:opacity-50"
                data-testid="account-status-close"
              >
                <XCircle className="h-4 w-4" /> Close account
              </button>
            )}
          </div>
        </PopoverContent>
      </Popover>
      {lastChangeLine && (
        <span className="text-[11px] text-gray-400 mt-1" data-testid="account-status-last-change">
          {lastChangeLine}
        </span>
      )}
      <AccountCloseDialog
        isOpen={closeDialogOpen}
        lead={lead}
        submitting={submitting}
        onCancel={handleCloseDialogCancel}
        onConfirm={handleCloseDialogConfirm}
      />
    </span>
  );
};

export default AccountStatusMenu;
